import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Building2,
  MapPin,
  DollarSign,
  Utensils,
  CheckCircle2,
  Phone,
  Link as LinkIcon,
  UploadCloud,
  Loader2,
  X
} from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/misc';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import LocationPicker from '@/components/rental/LocationPicker';
import { cn } from '@/lib/utils';
import { useUIStore } from '@/store/uiStore';
import { FileUploadDropzone } from '@/components/common';
import { NumberInput } from '@/components/ui/numberInput';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import {
  useCreateRentalProperty,
  useUpdateRentalProperty,
  useRentalProperty,
} from '@/hooks/useRental';
import { useProvinces, useDistricts, useWards } from '@/hooks/useAddress';
import { addressApi } from '@/api/addressApi';
import { imageApi } from '@/api/imageApi';

/* ─── constants ─── */
const PROPERTY_TYPES = [
  { value: 'commercial', label: 'Mặt bằng thương mại' },
  { value: 'house', label: 'Nhà phố' },
  { value: 'apartment', label: 'Căn hộ' },
  { value: 'shophouse', label: 'Shophouse' },
  { value: 'villa', label: 'Villa' },
  { value: 'office', label: 'Văn phòng' },
  { value: 'warehouse', label: 'Kho / Xưởng' },
  { value: 'other', label: 'Khác' },
];
const BUSINESS_TYPES = [
  { value: 'restaurant', label: 'Nhà hàng' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'bar', label: 'Bar / Lounge' },
  { value: 'bakery', label: 'Tiệm bánh' },
  { value: 'food_court', label: 'Food Court' },
  { value: 'retail', label: 'Bán lẻ' },
  { value: 'other', label: 'Khác' },
];
const AREA_UNITS = [
  { value: 'm²', label: 'm²' },
  { value: 'ft²', label: 'ft²' },
];
const CURRENCIES = [
  { value: 'VND', label: 'VND' },
  { value: 'USD', label: 'USD' },
];

/* ─── zod schema ─── */
const numReq = (errMsg: string) => z.preprocess(
  (val) => {
    if (val === '' || val === undefined || val === null) return undefined;
    if (typeof val === 'string') val = val.replace(/,/g, '');
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  },
  z.number({ required_error: errMsg }).positive(errMsg)
);

const schema = z.object({
  title: z.string({ required_error: 'Bắt buộc nhập' }).min(5, 'Tối thiểu 5 ký tự'),
  description: z.string().optional().or(z.literal('')),
  propertyType: z.string({ required_error: 'Bắt buộc nhập' }).min(1, 'Chọn loại bất động sản'),
  businessType: z.string({ required_error: 'Bắt buộc nhập' }).min(1, 'Chọn loại hình kinh doanh'),

  streetAddress: z.string({ required_error: 'Bắt buộc nhập' }).min(5, 'Nhập địa chỉ'),
  countryId: z.coerce.number().default(1),
  provinceId: z.coerce.number({ required_error: 'Bắt buộc nhập', invalid_type_error: 'Bắt buộc nhập' }).min(1, 'Chọn tỉnh/thành'),
  districtId: z.coerce.number({ required_error: 'Bắt buộc nhập', invalid_type_error: 'Bắt buộc nhập' }).min(1, 'Chọn quận/huyện'),
  wardId: z.coerce.number({ required_error: 'Bắt buộc nhập', invalid_type_error: 'Bắt buộc nhập' }).min(1, 'Chọn phường/xã'),

  area: numReq('Bắt buộc nhập diện tích'),
  areaUnit: z.string().default('m²'),
  seatingCapacity: z.coerce.number().optional(),

  monthlyRent: numReq('Bắt buộc nhập giá thuê'),
  currencyUnit: z.string().default('VND'),
  availableFrom: z.string({ required_error: 'Bắt buộc nhập' }).min(1, 'Bắt buộc nhập'),

  hasHoodSystem: z.boolean().default(false),
  gasConnection: z.boolean().default(false),
  hasLiquorLicense: z.boolean().default(false),

  hasEmployeeBathroom: z.boolean().default(false),
  hasCustomerBathroom: z.boolean().default(false),
  hasOutdoorSeating: z.boolean().default(false),
  hasParking: z.boolean().default(false),
  hasStorage: z.boolean().default(false),

  contactEmail: z.string().email('Email không hợp lệ').optional().or(z.literal('')),
  contactPhone: z.string().optional().or(z.literal('')),
});

type FormData = z.infer<typeof schema>;

interface UploadedImg {
  id: string;
  url: string;
  isPrimary: boolean;
  file?: File;
}

/* ─── Checkbox component removed since we use MUI Checkbox ─── */

/* ─── FormCard ─── */
function FormCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border p-6 space-y-4" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
      <h2 className="font-display font-bold text-base flex items-center gap-2 pb-3 border-b" style={{ color: 'var(--text-1)', borderColor: 'var(--border-c)' }}>
        <span style={{ color: 'var(--brand)' }}>{icon}</span> {title}
      </h2>
      {children}
    </div>
  );
}

/* ─── Main Page ─── */
export default function CreateRentalPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useUIStore();

  const isEditMode = !!id;

  const { data: rawData } = useRentalProperty(id || '');
  const existingData = (rawData as any)?.data ?? rawData;
  const { mutate: createProperty, isPending: isCreating } = useCreateRentalProperty();
  const { mutate: updateProperty, isPending: isUpdating } = useUpdateRentalProperty();

  const [images, setImages] = useState<UploadedImg[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedGeo, setSelectedGeo] = useState<{ lat: string, lon: string, display_name: string } | null>(null);

  const isPending = isCreating || isUpdating || isUploading;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      countryId: 1,
      currencyUnit: 'VND',
      areaUnit: 'm²',
      hasStorage: false,
      hasHoodSystem: false,
      gasConnection: false,
      hasLiquorLicense: false,
      hasEmployeeBathroom: false,
      hasCustomerBathroom: false,
      hasOutdoorSeating: false,
      hasParking: false,
    },
  });

  useEffect(() => {
    if (isEditMode && existingData) {
      reset({
        title: existingData.title || '',
        description: existingData.description || '',
        propertyType: existingData.propertyType || '',
        businessType: existingData.businessType || '',
        streetAddress: existingData.streetAddress || '',
        countryId: existingData.countryId || 1,
        provinceId: existingData.provinceId || undefined,
        districtId: existingData.districtId || undefined,
        wardId: existingData.wardId || undefined,
        monthlyRent: existingData.monthlyRent || undefined,
        area: existingData.area || undefined,
        seatingCapacity: existingData.seatingCapacity || undefined,
        availableFrom: existingData.availableFrom ? existingData.availableFrom.split('T')[0] : '',
        currencyUnit: existingData.currencyUnit || 'VND',
        areaUnit: existingData.areaUnit || 'm²',
        hasStorage: existingData.hasStorage || false,
        hasHoodSystem: existingData.hasHoodSystem || false,
        gasConnection: existingData.gasConnection || false,
        hasLiquorLicense: existingData.hasLiquorLicense || false,
        hasEmployeeBathroom: existingData.hasEmployeeBathroom || false,
        hasCustomerBathroom: existingData.hasCustomerBathroom || false,
        hasOutdoorSeating: existingData.hasOutdoorSeating || false,
        hasParking: existingData.hasParking || false,
        contactEmail: existingData.contactEmail || '',
        contactPhone: existingData.contactPhone || '',
      });

      if (existingData.images?.length) {
        setImages(existingData.images.map((img: any) => ({
          id: img.id,
          url: img.image?.publicUrl || '',
          isPrimary: img.isPrimary,
        })));
      }

      if (existingData.latitude && existingData.longitude) {
        setSelectedGeo({
          lat: existingData.latitude.toString(),
          lon: existingData.longitude.toString(),
          display_name: existingData.streetAddress || '',
        });
      }
    }
  }, [isEditMode, existingData, reset]);

  const w = (field: keyof FormData) => watch(field);

  // For geocoding
  const countryId = watch('countryId') || 1;
  const provinceId = watch('provinceId');
  const districtId = watch('districtId');
  const wardId = watch('wardId');
  const streetAddress = watch('streetAddress');
  const { data: provinces } = useProvinces(countryId);
  const { data: districts } = useDistricts(provinceId);
  const { data: wards } = useWards(districtId);



  useEffect(() => {
    if (isEditMode && existingData) {
      reset({
        title: existingData.title || '',
        description: existingData.description || '',
        propertyType: existingData.propertyType || '',
        businessType: existingData.businessType || '',
        streetAddress: existingData.streetAddress || '',
        countryId: existingData.countryId || 1,
        provinceId: existingData.provinceId || 0,
        districtId: existingData.districtId || 0,
        wardId: existingData.wardId || 0,
        area: existingData.area,
        areaUnit: existingData.areaUnit || 'm²',
        monthlyRent: existingData.monthlyRent,
        currencyUnit: existingData.currencyUnit || 'VND',
        availableFrom: existingData.availableFrom ? new Date(existingData.availableFrom).toISOString().split('T')[0] : '',
        seatingCapacity: existingData.seatingCapacity,
        hasParking: !!existingData.hasParking,
        hasOutdoorSeating: !!existingData.hasOutdoorSeating,
        hasStorage: !!existingData.hasStorage,
        hasHoodSystem: !!existingData.hasHoodSystem,
        gasConnection: !!existingData.gasConnection,
        hasLiquorLicense: !!existingData.hasLiquorLicense,
        hasEmployeeBathroom: !!existingData.hasEmployeeBathroom,
        hasCustomerBathroom: !!existingData.hasCustomerBathroom,
        contactEmail: existingData.contactEmail || '',
        contactPhone: existingData.contactPhone || '',
      });

      if (existingData.images?.length) {
        setImages(
          existingData.images.map((img: any) => ({
            id: img.imageId || img.id,
            url: img.image?.publicUrl || '',
            isPrimary: img.isPrimary,
          }))
        );
      }
    }
  }, [isEditMode, existingData, reset]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newLocalImages: UploadedImg[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        addToast('error', `File ${file.name} không phải là hình ảnh hợp lệ.`);
        continue;
      }

      if (file.size > 1 * 1024 * 1024) {
        addToast('error', `Ảnh ${file.name} vượt quá dung lượng cho phép. Vui lòng chọn ảnh dưới 1MB.`);
        continue;
      }

      newLocalImages.push({
        id: Math.random().toString(36).substring(7),
        url: URL.createObjectURL(file),
        isPrimary: images.length === 0 && i === 0 && newLocalImages.length === 0,
        file,
      });
    }

    setImages((prev) => [...prev, ...newLocalImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const filtered = prev.filter((img) => img.id !== id);
      if (filtered.length > 0 && !filtered.find((img) => img.isPrimary)) {
        filtered[0].isPrimary = true;
      }
      return filtered;
    });
  };

  const setPrimaryImage = (id: string) => {
    setImages((prev) => prev.map((img) => ({ ...img, isPrimary: img.id === id })));
  };



  const onSubmit = async (data: FormData) => {
    try {
      setIsUploading(true);
      const finalImages = [];
      for (const img of images) {
        if (img.file) {
          const res = await imageApi.upload(img.file);
          if (res && res.data) {
            finalImages.push({ id: res.data.id, isPrimary: img.isPrimary });
          }
        } else {
          finalImages.push({ id: img.id, isPrimary: img.isPrimary });
        }
      }

      // Geocoding
      let latitude = selectedGeo?.lat;
      let longitude = selectedGeo?.lon;

      if (!latitude || !longitude) {
        try {
          const provinceName = provinces?.find((p) => p.id === data.provinceId)?.name || '';
          const districtName = districts?.find((d) => d.id === data.districtId)?.name || '';
          const wardName = wards?.find((w) => w.id === data.wardId)?.name || '';

          const fullAddress = [data.streetAddress, wardName, districtName, provinceName, 'Vietnam']
            .filter(Boolean)
            .join(', ');

          const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}`, {
            headers: {
              'Accept-Language': 'vi-VN,vi;q=0.9,en-US;q=0.8,en;q=0.7',
            }
          });
          const geoData = await res.json();

          if (geoData && geoData.length > 0) {
            latitude = geoData[0].lat;
            longitude = geoData[0].lon;
          }
        } catch (err) {
          console.error('Failed to geocode address', err);
        }
      }

      const payload = {
        ...data,
        availableFrom: data.availableFrom ? new Date(data.availableFrom).toISOString() : undefined,
        contactEmail: data.contactEmail || undefined,
        contactPhone: data.contactPhone || undefined,
        description: data.description || undefined,
        images: finalImages,
        latitude,
        longitude,
      };

      if (isEditMode && id) {
        updateProperty(
          { id, data: payload },
          {
            onSuccess: (res) => navigate(`/cho-thue/${res?.id || id}`)
          }
        );
      } else {
        createProperty(payload, {
          onSuccess: (res) => navigate(`/cho-thue/${res?.id || ''}`)
        });
      }
    } catch (err: any) {
      if (err?.response?.status === 413) {
        addToast('error', 'Ảnh tải lên quá lớn (vượt giới hạn 1MB của máy chủ). Vui lòng chọn ảnh nhỏ hơn.');
      } else {
        addToast('error', 'Lỗi khi tải ảnh lên. Vui lòng thử lại.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto animate-fade-in pb-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div>
          <h1 className="font-display font-bold text-2xl" style={{ color: 'var(--text-1)' }}>
            {isEditMode ? 'Cập nhật tin cho thuê' : 'Đăng tin cho thuê'}
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-3)' }}>
            Kết nối mặt bằng của bạn với khách thuê F&B
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* ── Thông tin cơ bản ── */}
        <FormCard title="Thông tin cơ bản" icon={<Building2 size={16} />}>
          <Input label="Tiêu đề tin đăng *" placeholder="VD: Mặt bằng cafe mặt tiền Quận 1 120m²" error={errors.title?.message} {...register('title')} />
          <Textarea
            label="Mô tả chi tiết"
            rows={4}
            placeholder="Mô tả vị trí, tình trạng mặt bằng, điểm nổi bật..."
            error={errors.description?.message}
            {...register('description')}
          />
          <div className="grid grid-cols-2 gap-4">
            <Select label="Loại bất động sản *" options={PROPERTY_TYPES} placeholder="-- Chọn loại --" error={errors.propertyType?.message} {...register('propertyType')} />
            <Select
              label="Loại hình kinh doanh *"
              options={BUSINESS_TYPES}
              placeholder="-- Chọn loại hình --"
              error={errors.businessType?.message}
              {...register('businessType')}
            />
          </div>
        </FormCard>

        {/* ── Địa điểm ── */}
        <FormCard title="Địa điểm" icon={<MapPin size={16} />}>
          <LocationPicker
            addressField="streetAddress"
            register={register}
            setValue={setValue}
            watch={watch}
            errors={errors}
            selectedGeo={selectedGeo}
            onSelectGeo={setSelectedGeo}
          />
        </FormCard>

        {/* ── Diện tích & Sức chứa ── */}
        <FormCard title="Diện tích & Sức chứa" icon={<Building2 size={16} />}>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <NumberInput label="Tổng diện tích *" placeholder="120" error={errors.area?.message} {...register('area')} />
            </div>
            <Select label="Đơn vị" options={AREA_UNITS} {...register('areaUnit')} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input label="Sức chứa (chỗ ngồi)" type="number" placeholder="50" {...register('seatingCapacity')} />
            <Input label="Ngày có thể thuê *" type="date" error={errors.availableFrom?.message} {...register('availableFrom')} />
          </div>
        </FormCard>

        {/* ── Tài chính ── */}
        <FormCard title="Thông tin tài chính" icon={<DollarSign size={16} />}>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <NumberInput
                label="Giá thuê / tháng *"
                placeholder="30,000,000"
                hint="VD: 30,000,000 = 30tr/tháng"
                error={errors.monthlyRent?.message}
                {...register('monthlyRent')}
              />
            </div>
            <Select label="Đơn vị tiền tệ" options={CURRENCIES} {...register('currencyUnit')} />
          </div>
        </FormCard>

        {/* ── Tiện nghi bếp & Phép ── */}
        <FormCard title="Tiện nghi bếp & Giấy phép" icon={<Utensils size={16} />}>
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
            <FormControlLabel
              control={<Checkbox checked={!!w('hasHoodSystem')} onChange={(e) => setValue('hasHoodSystem', e.target.checked)} sx={{ color: 'var(--border-c)', '&.Mui-checked': { color: 'var(--brand)' } }} />}
              label={<span className="text-sm font-medium">Hệ thống hút khói</span>}
            />
            <FormControlLabel
              control={<Checkbox checked={!!w('gasConnection')} onChange={(e) => setValue('gasConnection', e.target.checked)} sx={{ color: 'var(--border-c)', '&.Mui-checked': { color: 'var(--brand)' } }} />}
              label={<span className="text-sm font-medium">Đường ống gas</span>}
            />
            <FormControlLabel
              control={<Checkbox checked={!!w('hasLiquorLicense')} onChange={(e) => setValue('hasLiquorLicense', e.target.checked)} sx={{ color: 'var(--border-c)', '&.Mui-checked': { color: 'var(--brand)' } }} />}
              label={<span className="text-sm font-medium">Giấy phép rượu</span>}
            />
          </div>
        </FormCard>

        {/* ── Tiện ích & Liên hệ ── */}
        <FormCard title="Tiện ích & Đặc điểm" icon={<CheckCircle2 size={16} />}>
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-2">
            <FormControlLabel
              control={<Checkbox checked={!!w('hasEmployeeBathroom')} onChange={(e) => setValue('hasEmployeeBathroom', e.target.checked)} sx={{ color: 'var(--border-c)', '&.Mui-checked': { color: 'var(--brand)' } }} />}
              label={<span className="text-sm font-medium">Toilet nhân viên</span>}
            />
            <FormControlLabel
              control={<Checkbox checked={!!w('hasCustomerBathroom')} onChange={(e) => setValue('hasCustomerBathroom', e.target.checked)} sx={{ color: 'var(--border-c)', '&.Mui-checked': { color: 'var(--brand)' } }} />}
              label={<span className="text-sm font-medium">Toilet khách hàng</span>}
            />
            <FormControlLabel
              control={<Checkbox checked={!!w('hasOutdoorSeating')} onChange={(e) => setValue('hasOutdoorSeating', e.target.checked)} sx={{ color: 'var(--border-c)', '&.Mui-checked': { color: 'var(--brand)' } }} />}
              label={<span className="text-sm font-medium">Có khu ngoài trời</span>}
            />
            <FormControlLabel
              control={<Checkbox checked={!!w('hasParking')} onChange={(e) => setValue('hasParking', e.target.checked)} sx={{ color: 'var(--border-c)', '&.Mui-checked': { color: 'var(--brand)' } }} />}
              label={<span className="text-sm font-medium">Có bãi đậu xe</span>}
            />
            <FormControlLabel
              control={<Checkbox checked={!!w('hasStorage')} onChange={(e) => setValue('hasStorage', e.target.checked)} sx={{ color: 'var(--border-c)', '&.Mui-checked': { color: 'var(--brand)' } }} />}
              label={<span className="text-sm font-medium">Có khu vực kho</span>}
            />
          </div>
        </FormCard>

        {/* ── Hình ảnh minh hoạ ── */}
        <FormCard title="Hình ảnh minh hoạ (Tuỳ chọn)" icon={<LinkIcon size={16} />}>
          <div className="space-y-4">
            <FileUploadDropzone
              onFileSelect={(files) => handleFileUpload({ target: { files } } as any)}
              isUploading={isUploading}
              multiple
              accept="image/*,.pdf"
              title="Nhấn để chọn hoặc kéo thả ảnh vào đây"
              subtitle="Hỗ trợ JPG, PNG, PDF. Tối đa 5MB"
            />

            {images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                {images.map((img) => (
                  <div key={img.id} className={cn(
                    "relative aspect-video rounded-xl overflow-hidden border-2 group",
                    img.isPrimary ? "border-orange-500 shadow-md" : "border-gray-200"
                  )}>
                    <img src={img.url} alt="" className="w-full h-full object-cover" />

                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeImage(img.id); }}
                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-red-500 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                        title="Xóa ảnh"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    {img.isPrimary ? (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-orange-500 text-white text-[10px] font-bold rounded shadow-sm">
                        Ảnh chính
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPrimaryImage(img.id); }}
                        className="absolute top-2 left-2 px-2 py-1 bg-black/50 hover:bg-black/80 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Đặt làm ảnh chính
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </FormCard>

        <FormCard title="Liên hệ" icon={<Phone size={16} />}>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Email liên hệ" type="email" placeholder="owner@example.com" error={errors.contactEmail?.message} {...register('contactEmail')} />
            <Input label="Số điện thoại" placeholder="0901234567" error={errors.contactPhone?.message} {...register('contactPhone')} />
          </div>
        </FormCard>

        {/* Navigation */}
        <div className="flex gap-4 justify-end pt-4 mt-6 border-t" style={{ borderColor: 'var(--border-c)' }}>
          <Button type="button" variant="ghost" onClick={() => navigate('/cho-thue')} className="px-8 font-bold">
            Hủy
          </Button>
          <Button type="submit" variant="brand" loading={isPending} className="px-8 font-bold text-base shadow-lg shadow-orange-500/20">
            {isEditMode ? 'Lưu thay đổi' : 'Đăng tin cho thuê'}
          </Button>
        </div>
      </form>
    </div>
  );
}
